import * as Sentry from "@sentry/react-native";
import { router } from "expo-router";
import { observer } from "mobx-react";
import { useEffect, useLayoutEffect } from "react";
import { FormattedMessage } from "react-intl";
import { StyleSheet } from "react-native";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { BodyText } from "~/components/V2/Text/BodyText";
import { BrandText } from "~/components/V2/Text/BrandText";
import { View } from "~/components/V2/View";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { assert } from "~/utils/assert";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import CheckIcon from "./svgs/CheckIcon";
import { useProgressBarStep } from "./useProgressBarStep";
import { useStandaloneBookingFlow } from "./useStandaloneBookingFlow";
import { withBookingData } from "./withBookingData";

const S = StyleSheet.create({
  cardStyle: {
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    backgroundColor: styleConstantsV2.colorPrimary,
  },
  closeIconContainer: { zIndex: 1 },
  checkIconContainer: { paddingTop: 100 },
  subtitle: { fontSize: 18, lineHeight: 28 },
  buttonText: { fontFamily: "AlbertSans-Bold" },
});

const SuccessScreen = observer(function SuccessScreen() {
  const { isStandAloneBookingFlow } = useStandaloneBookingFlow();
  const store = useStore();

  const activeUser = store.authStore.activeUser;

  const { patientId, appointmentId, clinicName } = store.bookingStore;

  useEffect(() => {
    try {
      assert(activeUser, "Active user is not set, can not log booking event");

      // Log the event to the booking data layer
      bookingDataLayerPush({
        event: "Booking_web_app",
        ga_action: "Confirm appointment",
        ga_label: clinicName as string,
        clinic_name: clinicName as string,
        appointment_id: appointmentId as string,
        client_id: activeUser.id,
        patient_id: patientId as string,
        // customer_email: activeUser.email ?? "",
        // customer_phone: activeUser.phoneNumber,
      });
    } catch (error) {
      // Do not show the error to a user if tracking step fails
      Sentry.captureException(error);
      console.log("Error: ", error);
    }
    // log only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalProgressBarSteps = store.bookingStore.totalProgressBarSteps;

  // Success screen is the last step in the booking flow
  // so we set the currentProgressBarStep to the totalProgressBarSteps
  useProgressBarStep(totalProgressBarSteps);

  useLayoutEffect(() => {
    store.bookingStore.setShowHeader(false);

    return () => {
      store.bookingStore.setShowHeader(true);
    };
  }, [store.bookingStore]);

  return (
    <View colorPrimary flex>
      <View centerContent style={S.checkIconContainer}>
        <CheckIcon />
      </View>
      <Spacer extraLarge />

      <View centerContent paddingHorizontal32>
        <BrandText sizeH2 weightExtraBold colorWhite alignCenter>
          <FormattedMessage
            id="9biY42"
            defaultMessage="Thank you for your booking"
            description="Thank you for your booking message on scuccess screen"
          />
        </BrandText>
        <Spacer large />

        <BodyText style={S.subtitle} colorWhite alignCenter>
          <FormattedMessage
            id="zkaOOo"
            defaultMessage="Your appointment is now booked. We look forward to seeing you and your pet soon."
            description="Subtitle for successfull booking screen"
          />
        </BodyText>
      </View>
      {isStandAloneBookingFlow === false && (
        <View
          paddingHorizontal24
          flex
          flexDirectionColumnReverse
          style={{ paddingBottom: 64 }}
        >
          <ButtonV2
            medium
            primaryReversed
            onPress={() => {
              router.dismissAll();

              router.replace("/(app)");
            }}
            title={intl.formatMessage({
              id: "4BH2f8",
              defaultMessage: "Close",
              description: "Close button text",
            })}
            textStyle={S.buttonText}
          />
        </View>
      )}
    </View>
  );
});

// If any required data from earlier in the booking process is missing, redirect to the first step
export const SuccessScreenScreenWithBookingData = withBookingData(
  SuccessScreen,
  ["clinicId", "patientId", "reasonId", "practitionerId", "start", "end"],
);
